/**
 * Which `ReviewPageCards` fields the `/review` belt renders as a card (TECHNICAL_DEBT §0ck).
 *
 * `reviewPageCards()` is read by the belt, the everything export and the MCP, and two of its
 * fields are for the export only: the belt has no card for them (the owner's no-card rule). The
 * Dashboard says nothing `/review` does not, so it reads this list, and so does the parity check
 * that compares the two: a Dashboard row for an export-only field fails it.
 *
 * Keyed by every field, so a new card is a decision here before it compiles. Each `true` key is
 * tied to the belt template's predicate for it by `review-belt-cards.test.ts`, and each `false` key
 * to the template's not naming it.
 */

import type { ReviewPageCards } from './review-page-model';

export type ReviewCardKey = keyof ReviewPageCards;

export const REVIEW_CARD_ON_BELT: { readonly [K in ReviewCardKey]-?: boolean } = {
  comparisonWithheld: true,
  /** Export only: the pair's Cannibalization card covers it, or the fault is our search pick. */
  comparisonOnAnotherPage: false,
  supportingPages: true,
  sections: true,
  sectionCompareNotice: true,
  /** Export only: a section the page already has is not an issue (§0cb; owner, 2026-10-19). */
  alreadyOnPage: false,
  freshness: true,
  answerStructure: true,
  pageType: true,
  technicalIssues: true,
  technicalWarnings: true,
  internalLinks: true,
  cannibalization: true,
  nearMissSearches: true,
  signals: true,
  /** Context, not a finding: no actions (TRAFFIC_ESTIMATE_CUTOVER.md D3, owner 2026-11-03). */
  estimateBesideRival: true
};

/** Whether a card value is shown: not null, and not an empty list. */
export function reviewCardShown(value: unknown): boolean {
  if (value === null || value === undefined) return false;
  return !(Array.isArray(value) && value.length === 0);
}

/** Whether the belt shows this card for the page: a belt card, and shown. */
export function reviewBeltShowsCard(cards: ReviewPageCards, key: ReviewCardKey): boolean {
  return REVIEW_CARD_ON_BELT[key] === true && reviewCardShown(cards[key]);
}
